<template>
  <div class="policy-change-form">
    <!-- Header Section -->
    <div class="bg-blue-50 py-8 px-4 border-b border-blue-100">
      <div class="max-w-4xl mx-auto">
        <h1 class="text-2xl md:text-3xl font-bold text-center text-blue-800 mb-2">
          {{ title }}
        </h1>
        <p v-if="description" class="text-center text-gray-600 max-w-2xl mx-auto">
          {{ description }}
        </p>
        
        <!-- Steps indicator -->
        <div v-if="steps.length > 1 && currentStep !== 'success'" class="mt-8">
          <div class="flex items-center justify-center">
            <div
              v-for="(step, index) in steps"
              :key="index"
              class="flex items-center"
            >
              <!-- Step circle -->
              <div
                :class="[
                  'rounded-full w-8 h-8 flex items-center justify-center font-medium text-sm',
                  getStepCircleClass(step.number)
                ]"
              >
                <template v-if="step.number < currentStep">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                </template>
                <template v-else>
                  {{ step.number }}
                </template>
              </div>
              
              <!-- Step label -->
              <span
                :class="[
                  'hidden md:block ml-2 text-sm',
                  currentStep >= step.number ? 'text-blue-800' : 'text-gray-500'
                ]"
              >
                {{ step.label }}
              </span>
              
              <!-- Connector line -->
              <div
                v-if="index < steps.length - 1"
                :class="[
                  'w-12 md:w-16 h-1 mx-2',
                  currentStep > step.number ? 'bg-blue-500' : 'bg-gray-300'
                ]"
              ></div>
            </div>
          </div>
          
          <!-- Mobile step label -->
          <div class="md:hidden text-center mt-2 text-blue-800 font-medium">
            {{ getCurrentStepLabel() }}
          </div>
        </div>
      </div>
    </div>
    
    <!-- Main Content Section -->
    <div class="max-w-4xl mx-auto py-6 px-4">
      <!-- Dynamic step content -->
      <div v-if="currentStep === 'success'">
        <slot name="success"></slot>
      </div>
      <div v-else-if="currentStep === 1">
        <slot name="step1"></slot>
      </div>
      <div v-else-if="currentStep === 2">
        <slot name="step2"></slot>
      </div>
      <div v-else-if="currentStep === 3">
        <slot name="step3"></slot>
      </div>
      <div v-else-if="currentStep === 4">
        <slot name="step4"></slot>
      </div>
      <div v-else-if="currentStep === 5">
        <slot name="step5"></slot>
      </div>
      
      <!-- Navigation Buttons -->
      <div 
        v-if="currentStep !== 'success'" 
        class="mt-8 flex justify-between"
      >
        <button
          @click="handleBack"
          class="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded"
        >
          Back
        </button>
        
        <button
          v-if="isLastStep"
          @click="handleSubmit"
          class="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
        >
          Submit
        </button>
        <button
          v-else
          @click="handleNext"
          class="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
        >
          Next
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  title: {
    type: String,
    required: true
  },
  description: {
    type: String,
    default: ''
  },
  steps: {
    type: Array,
    default: () => []
  },
  currentStep: {
    type: [Number, String],
    required: true
  }
});

const emit = defineEmits(['next', 'back', 'submit']);

// Computed property to determine if current step is the last one
const isLastStep = computed(() => {
  if (typeof props.currentStep === 'string') {
    return false;
  }
  return props.currentStep === props.steps.length;
});

// Get class for step circle based on current progress
const getStepCircleClass = (stepNumber) => {
  if (props.currentStep >= stepNumber) {
    return 'bg-blue-500 text-white';
  }
  return 'bg-gray-200 text-gray-600';
};

// Get current step label for mobile view
const getCurrentStepLabel = () => {
  if (typeof props.currentStep === 'string') {
    return '';
  }
  
  const currentStepObj = props.steps.find(step => step.number === props.currentStep);
  return currentStepObj ? currentStepObj.label : '';
};

// Handle next button click
const handleNext = () => {
  emit('next');
};

// Handle back button click
const handleBack = () => {
  emit('back');
};

// Handle submit button click
const handleSubmit = () => {
  emit('submit');
};
</script>

<style scoped>
.policy-change-form {
  min-height: calc(100vh - 100px);
  display: flex;
  flex-direction: column;
}
</style> 